import { Controller } from 'stimulus'
import { enter, leave } from 'el-transition'
import $ from 'jquery'
import './new_contribution_component.css'

export default class extends Controller {
  static targets = ['formContainer', 'button', 'underlay', 'attachedRecordings']
  // Fetched because someone clicked the collapsed composer (CommentsComponent lazy_composer):
  // open right away, as that click would have.
  static values = { openOnConnect: Boolean }

  // Ground truth for the leave().then() guards below, independent of the `hidden` class those
  // same transitions are mutating. Read off the DOM so a `start_in` form (rendered already open)
  // doesn't look closed to a hideForm() called before any showForm().
  #open = false

  connect() {
    this.#open = !this.formContainerTarget.classList.contains('hidden')
    if (window.location.search.includes('debug_new_contribution'))
      this.buttonTarget.click()
    this.updateBodyRequiredState()
    if (this.openOnConnectValue) {
      this.openOnConnectValue = false // Only once: not again if the form is moved (feed modal)
      if (!this.#open) {
        this.showForm()
        this.#focusEditorOnceItsControllerConnects()
      }
    }
  }

  // showForm() asks the editor for focus with an editor:focus event. In a fetched composer the
  // editor's controller can still be loading (its code loads on demand) and miss it: ask again
  // once it has connected, unless focus has already gone into the form.
  async #focusEditorOnceItsControllerConnects() {
    const editor = this.formContainerTarget.querySelector(
      '.js-tiptap-editor-container'
    )
    const deadline = performance.now() + 10000 // Then give up: the form is open, a click focuses it
    while (editor && performance.now() < deadline) {
      if (
        this.application?.getControllerForElementAndIdentifier(
          editor,
          'tiptap--editor'
        )
      ) {
        if (
          this.#open &&
          !this.formContainerTarget.contains(document.activeElement)
        )
          editor.dispatchEvent(new CustomEvent('editor:focus'))
        return
      }
      await new Promise((resolve) => requestAnimationFrame(resolve))
    }
  }

  showForm() {
    this.#open = true
    // leave() only adds the `hidden` class once its ~300ms CSS transition settles, not when it
    // starts. A hideForm() landing inside that window clears `hidden` on the button
    // synchronously, but this still-pending leave() would otherwise put it right back once it
    // settles — stranding the button hidden even though hideForm() re-showed it.
    leave(this.buttonTarget).then(() => {
      if (!this.#open) this.buttonTarget.classList.remove('hidden')
    })
    enter(this.formContainerTarget)
    if (this.hasUnderlayTarget) enter(this.underlayTarget)
    // The tiptap editor adds a input field with opacity 0 so we can use the HTML required validation like it is on the editor contenteditable div
    // So that would be the first input field which we don't want to focus, so added not("input[tabindex='-1']")
    const $focusable = $(this.formContainerTarget)
      .find('input:visible, textarea:visible, [contenteditable]')
      .not("input[tabindex='-1']")
      .first()
    if ($focusable.length) {
      $focusable.focus()
    } else {
      // The editor initializes lazily when visible, so a form hidden until now has no contenteditable yet
      this.formContainerTarget
        .querySelector('.js-tiptap-editor-container')
        ?.dispatchEvent(new CustomEvent('editor:focus'))
    }
    this.dispatch('formShown')
  }

  hideForm() {
    // If the mentions menu is open on editor then the form should not be closed, instead the menu will be closed automatically on click
    if (this.element.querySelector('.js-mentions .tippy-content')) {
      return
    }

    this.#open = false
    // Same race as showForm()'s leave(buttonTarget) above, mirrored for the form/underlay: a
    // showForm() landing before these settle must not have its enter() undone once they do.
    leave(this.formContainerTarget).then(() => {
      if (this.#open) this.formContainerTarget.classList.remove('hidden')
    })
    if (this.hasUnderlayTarget) {
      leave(this.underlayTarget).then(() => {
        if (this.#open) this.underlayTarget.classList.remove('hidden')
      })
    }
    enter(this.buttonTarget)
  }

  resetForm() {
    this.attachedRecordingsTarget.innerHTML = ''
    this.updateBodyRequiredState()
  }

  openDropzoneFilePicker() {
    this.element.querySelector('.dropzone').click()
  }

  updateBodyRequiredState() {
    const tiptapInput = this.element.querySelector('.js-tiptap-editor-input')
    const hasRecordings = this.attachedRecordingsTarget.children.length > 0
    const dropzoneInput = this.element.querySelector(
      '[data-community--dropzone-target="input"]'
    )
    const hasAttachments = dropzoneInput?.value?.length > 0
    const embedInput = this.element.querySelector('input[id$="embed_ids_"]')
    const hasEmbeds = embedInput?.value?.length > 0
    // The forum post composer's poll: its _destroy field is "0" while the poll is on.
    // A poll with votes renders no fields, only a [data-poll-locked] notice: it stays on the post.
    const pollDestroy = this.element.querySelector(
      'input[name$="[poll_attributes][_destroy]"]'
    )
    const hasPoll =
      pollDestroy?.value === '0' ||
      this.element.querySelector('[data-poll-locked]') !== null

    if (tiptapInput) {
      tiptapInput.required = !(
        hasRecordings ||
        hasAttachments ||
        hasEmbeds ||
        hasPoll
      )
      this.element
        .querySelector('.ProseMirror')
        ?.setAttribute('aria-required', String(tiptapInput.required))
    }
  }
}
